import React from 'react';

import {
  LoaderStage,
  LoaderTestimonial,
  LoadingAnalysisConfig,
  newQuizItemId,
} from '../../CustomBlocks/quiz/quizTypes';
import { ClockIcon } from '../../shared/Icons';
import { NumberStepper, SettingsGroup, SettingsRow, Switch } from '../slotBookingEditor/settingsUi';
import { TILE } from '../slotBookingEditor/SlotBookingEditor.styles';
import { ListSection, TextSetting } from './quizEditorUi';

interface LoadingAnalysisEditorProps {
  config: LoadingAnalysisConfig;
  onChange: (patch: Partial<LoadingAnalysisConfig>) => void;
}

const LoadingAnalysisEditor: React.FC<LoadingAnalysisEditorProps> = ({ config, onChange }) => (
  <>
    <SettingsGroup title="Text" footer={<>Use <code>{'{{field_name}}'}</code> to insert an answer.</>}>
      <TextSetting label="Title" value={config.title} onChange={(title) => onChange({ title })} />
      <TextSetting
        label="Social proof line"
        description="Shown under the bars. Basic HTML like <b> is allowed."
        value={config.subtitle}
        onChange={(subtitle) => onChange({ subtitle })}
      />
    </SettingsGroup>

    <ListSection<LoaderStage>
      title="Progress stages"
      itemName="Stage"
      items={config.stages}
      minItems={1}
      create={() => ({ id: newQuizItemId(), label: 'New stage' })}
      onChange={(stages) => onChange({ stages })}
      renderItem={(stage, update) => (
        <input
          type="text"
          className="sb-text-input"
          value={stage.label}
          onChange={(e) => update({ label: e.target.value })}
        />
      )}
    />

    <SettingsGroup title="Timing">
      <SettingsRow
        icon={<ClockIcon size={16} />}
        iconColor={TILE.orange}
        label="Each stage takes"
      >
        <NumberStepper
          value={config.secondsPerStage}
          min={1}
          max={15}
          step={1}
          unit="sec"
          label="Seconds per stage"
          onChange={(secondsPerStage) => onChange({ secondsPerStage })}
        />
      </SettingsRow>
      <SettingsRow
        label="Continue automatically"
        description="Go to the next step when all stages finish"
      >
        <Switch
          checked={config.autoAdvance}
          label="Continue automatically"
          onChange={(autoAdvance) => onChange({ autoAdvance })}
        />
      </SettingsRow>
    </SettingsGroup>

    <ListSection<LoaderTestimonial>
      title="Testimonials"
      itemName="Testimonial"
      items={config.testimonials}
      footer="They rotate while the stages fill. Leave empty to hide."
      create={() => ({ id: newQuizItemId(), name: 'Name', text: 'What they said' })}
      onChange={(testimonials) => onChange({ testimonials })}
      renderItem={(item, update) => (
        <>
          <textarea
            className="qe-textarea"
            value={item.text}
            placeholder="Quote"
            onChange={(e) => update({ text: e.target.value })}
          />
          <div className="qe-inline">
            <input
              type="text"
              className="sb-text-input"
              value={item.name}
              placeholder="Name"
              onChange={(e) => update({ name: e.target.value })}
            />
            <input
              type="url"
              className="sb-text-input"
              value={item.imageUrl || ''}
              placeholder="Photo URL (optional)"
              onChange={(e) => update({ imageUrl: e.target.value })}
            />
          </div>
        </>
      )}
    />
  </>
);

export default LoadingAnalysisEditor;
